import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";

import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ListAltIcon from "@mui/icons-material/ListAlt";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { DataGrid } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axios from "axios";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import InternalPostStatusDialog from "./InternalPostStatusDialog";
import StaffAllocationDialog from "../ExamExternalSchedule/StaffAllocationDialog";
import { minWidth } from "@mui/system";

const emptyMaster = {
  program_id: "",
  component_id: "",
  exam_mode_id: "",
  assessment_type: "INTERNAL",
  exam_month: "",
  exam_year: new Date().getFullYear(),
  exam_start_date: "",
  exam_end_date: "",
  start_enroll_date: "",
  end_enroll_date: "",
  exclude_dates: [],
  workflow_status: "DRAFT",
  remarks: "",
};

const emptyDetail = {
  year_number: "",
  semester_number: "",
  number_of_subjects: 1,
  slotIds: [""],
  admitted_batches: [],
};

const seatingControlSx = {
  "& .MuiInputLabel-root": { bgcolor: "background.paper", px: 0.5 },
  "& .MuiOutlinedInput-root": { height: 40 },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    height: "auto !important",
    minHeight: "0 !important",
    boxSizing: "border-box",
  },
};

const isoDate = (value) => (value ? new Date(value).toISOString().slice(0, 10) : "");
const displayDate = (value) => {
  const date = isoDate(value);
  return date ? date.split("-").reverse().join("-") : "";
};

const examMonths = Array.from({ length: 8 }, (_, index) => {
  const date = new Date();
  date.setMonth(date.getMonth() - 2 + index);
  return date
    .toLocaleString("default", { month: "long", year: "numeric" })
    .toUpperCase()
    .replace(" ", "-");
});

function Button({ sx, variant = "contained", ...props }) {
  return (
    <MuiButton
      variant={variant}
      {...props}
      sx={{
        color: "#fff",
        backgroundColor: "#1976d2",
        borderColor: "#1976d2",
        "&:hover": { color: "#fff", backgroundColor: "#1565c0", borderColor: "#1565c0" },
        ...sx,
      }}
    />
  );
}

Button.propTypes = {
  sx: PropTypes.object,
  variant: PropTypes.string,
};

function RowActions({ row, onCreateSemester, onViewSemester, onViewTimetable, onEdit, onDelete }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const close = () => setAnchorEl(null);
  const run = (action) => {
    close();
    action(row);
  };

  return (
    <>
      <Button
        variant="contained"
        size="medium"
        startIcon={<MoreVertIcon />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          color: "#fff",
          textTransform: "none",
          "&:hover": { color: "#fff" },
        }}
      >
        Actions
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
        <MenuItem onClick={() => run(onEdit)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit MID Exam Schedule
        </MenuItem>
        <MenuItem onClick={() => run(onDelete)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          Delete MID Exam Schedule
        </MenuItem>
        <MenuItem onClick={() => run(onCreateSemester)}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          Create Semester Schedule
        </MenuItem>
        <MenuItem onClick={() => run(onViewSemester)}>
          <ListItemIcon>
            <ListAltIcon fontSize="small" />
          </ListItemIcon>
          View Semester Schedules
        </MenuItem>
        <MenuItem onClick={() => run(onViewTimetable)}>
          <ListItemIcon>
            <ListAltIcon fontSize="small" />
          </ListItemIcon>
          Show Exam Dates
        </MenuItem>
      </Menu>
    </>
  );
}

RowActions.propTypes = {
  row: PropTypes.object.isRequired,
  onCreateSemester: PropTypes.func.isRequired,
  onViewSemester: PropTypes.func.isRequired,
  onViewTimetable: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

function ExamDateActions({ row, master, onOpen, onDownload, onDaywiseStrength }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const preset = {
    from_exam_date: isoDate(row.from_exam_date),
    exam_slot_id: row.exam_slot_id,
  };
  const actions = [
    ["seating-plan", "Seating Plan", PrintIcon],
    ["abstract", "Abstract Seating Plan", PrintIcon],
    ["room-course-strength", "Room Course Strength", ScheduleIcon],
    ["attendance-sheet", "Attendance Sheet", ScheduleIcon],
    ["view-invigilators", "View Invigilators", ScheduleIcon],
    ["post-absents", "Post Absents", ScheduleIcon],
    ["post-malpractice", "Post MalPractice", ScheduleIcon],
    ["post-debar", "Post Debar", ScheduleIcon],
    ["dform", "DForm", ScheduleIcon],
    ["daywise-strength", "Daywise Strength", ScheduleIcon],
  ];
  return (
    <>
      <IconButton size="medium" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {actions.map(([mode, label, ActionIcon]) => (
          <MenuItem
            key={mode}
            sx={mode === "daywise-strength" ? { color: "#c62828", fontWeight: "bold" } : undefined}
            onClick={() => {
              setAnchorEl(null);
              if (mode === "daywise-strength") {
                onDaywiseStrength();
                return;
              }
              if (["room-course-strength", "attendance-sheet", "dform"].includes(mode)) {
                onDownload(master, preset, mode, "pdf");
                return;
              }
              onOpen(master, mode, preset);
            }}
          >
            <ActionIcon fontSize="small" sx={{ mr: 1 }} />
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

ExamDateActions.propTypes = {
  row: PropTypes.object.isRequired,
  master: PropTypes.object.isRequired,
  onOpen: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onDaywiseStrength: PropTypes.func.isRequired,
};

export default function InternalExamSchedule() {
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const apiBase = process.env.REACT_APP_API_BASE_URL;
  const baseUrl = `${apiBase}/api/exam-internal-schedule/${user.client_id}/${user.college_id}`;
  const [rows, setRows] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [components, setComponents] = useState([]);
  const [examModes, setExamModes] = useState([]);
  const [slots, setSlots] = useState([]);
  const [admittedBatches, setAdmittedBatches] = useState([]);
  const [academicYear, setAcademicYear] = useState(null);
  const [yearSemesters, setYearSemesters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [masterOpen, setMasterOpen] = useState(false);
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const [editingMaster, setEditingMaster] = useState(null);
  const [masterForm, setMasterForm] = useState(emptyMaster);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedMaster, setSelectedMaster] = useState(null);
  const [details, setDetails] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editingDetail, setEditingDetail] = useState(null);
  const [detailForm, setDetailForm] = useState(emptyDetail);
  const [timetableOpen, setTimetableOpen] = useState(false);
  const [timetableMaster, setTimetableMaster] = useState(null);
  const [timetableRows, setTimetableRows] = useState([]);
  const [timetableLoading, setTimetableLoading] = useState(false);
  const [timetableCollege, setTimetableCollege] = useState(null);
  const [dateEditRow, setDateEditRow] = useState(null);
  const [dateEditValue, setDateEditValue] = useState("");
  const [slotEditRow, setSlotEditRow] = useState(null);
  const [slotEditValue, setSlotEditValue] = useState("");

  const [seatingOpen, setSeatingOpen] = useState(false);
  const [seatingMaster, setSeatingMaster] = useState(null);
  const [seatingTimetables, setSeatingTimetables] = useState([]);
  const [seatingRows, setSeatingRows] = useState([]);
  const [seatingLoading, setSeatingLoading] = useState(false);
  const [seatingMode, setSeatingMode] = useState("seating-plan");
  const [attendanceSearch, setAttendanceSearch] = useState("");
  const [seatingForm, setSeatingForm] = useState({
    from_exam_date: "",
    exam_slot_id: "",
    columns_per_room: "",
    invigilator_strength: "",
  });
  const [notice, setNotice] = useState({ open: false, message: "", severity: "success" });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const notify = (message, severity = "success") => setNotice({ open: true, message, severity });

  const loadSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(baseUrl);
      setRows(
        (data.data?.items || []).map((item) => ({
          ...item,
          id: item.internal_schedule_master_id,
          component_name: item.component?.component_name || "",
          program_name: item.program?.program_name || "All Programs",
          exam_mode_name: item.exam_mode?.exam_mode_name || "",
        }))
      );
    } catch (error) {
      notify(error.response?.data?.message || "Failed to load internal schedules", "error");
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    if (!user.client_id || !user.college_id) return;
    loadSchedules();
    Promise.allSettled([
      axios.get(`${baseUrl}/lookups`),
      axios.get(`${apiBase}/api/programs/${user.client_id}`, {
        params: { college_id: user.college_id },
      }),
      axios.get(`${apiBase}/api/exam-modes/${user.client_id}/${user.college_id}`),
      axios.get(
        `${apiBase}/api/admittedbatches/academic-year/${user.client_id}/${user.college_id}`
      ),
    ]).then(([lookupResult, programResult, modeResult, academicYearResult]) => {
      if (lookupResult.status === "fulfilled") {
        setComponents(lookupResult.value.data.data?.components || []);
        setSlots(lookupResult.value.data.data?.slots || []);
        setAdmittedBatches(lookupResult.value.data.data?.admittedBatches || []);
      }
      if (programResult.status === "fulfilled") setPrograms(programResult.value.data || []);
      if (modeResult.status === "fulfilled") setExamModes(modeResult.value.data.data?.items || []);
      if (academicYearResult.status === "fulfilled")
        setAcademicYear(academicYearResult.value.data.data || null);

      const failed = [lookupResult, programResult, modeResult, academicYearResult].filter(
        (result) => result.status === "rejected"
      );
      if (failed.length) {
        notify(
          failed[0].reason?.response?.data?.message ||
            `${failed.length} schedule lookup request${failed.length > 1 ? "s" : ""} failed`,
          "error"
        );
      }
    });
  }, [apiBase, baseUrl, loadSchedules, user.client_id, user.college_id]);

  const openAddMaster = () => {
    setEditingMaster(null);
    setMasterForm({ ...emptyMaster });
    setExcludeDialogOpen(false);
    setMasterOpen(true);
  };

  const openEditMaster = (row) => {
    setEditingMaster(row);
    setMasterForm({
      ...row,
      exam_start_date: isoDate(row.exam_start_date),
      exam_end_date: isoDate(row.exam_end_date),
      start_enroll_date: isoDate(row.start_enroll_date),
      end_enroll_date: isoDate(row.end_enroll_date),
      exclude_dates: Array.isArray(row.exclude_dates) ? row.exclude_dates : [],
    });
    setExcludeDialogOpen(false);
    setMasterOpen(true);
  };

  const handleExcludedDateChange = (date) => {
    if (!date) return;
    const formattedDate = dayjs(date).format("YYYY-MM-DD");
    setMasterForm((current) => {
      const selectedDates = Array.isArray(current.exclude_dates) ? current.exclude_dates : [];
      const excludeDates = selectedDates.includes(formattedDate)
        ? selectedDates.filter((item) => item !== formattedDate)
        : [...selectedDates, formattedDate];
      excludeDates.sort((first, second) => dayjs(first).diff(dayjs(second)));
      return { ...current, exclude_dates: excludeDates };
    });
  };

  const saveMaster = async () => {
    const required = [
      "component_id",
      "exam_mode_id",
      "exam_month",
      "exam_year",
      "exam_start_date",
      "exam_end_date",
    ];
    if (required.some((field) => !masterForm[field])) {
      notify("Please complete all required schedule fields", "warning");
      return;
    }
    if (masterForm.exam_end_date < masterForm.exam_start_date) {
      notify("Exam end date cannot be before exam start date", "warning");
      return;
    }
    const payload = {
      ...masterForm,
      start_enroll_date: masterForm.exam_start_date,
      end_enroll_date: masterForm.exam_start_date,
      created_by: user.user_email,
      modified_by: user.user_email,
    };
    try {
      if (editingMaster) await axios.put(`${baseUrl}/${editingMaster.id}`, payload);
      else await axios.post(baseUrl, payload);
      setMasterOpen(false);
      notify(editingMaster ? "Internal schedule updated" : "Internal schedule created");
      loadSchedules();
    } catch (error) {
      notify(error.response?.data?.message || "Failed to save internal schedule", "error");
    }
  };

  const deleteMaster = async (row) => {
    if (!window.confirm("Delete this internal exam schedule?")) return;
    try {
      await axios.delete(`${baseUrl}/${row.id}`);
      notify("Internal schedule deleted");
      loadSchedules();
    } catch (error) {
      notify(error.response?.data?.message || "Failed to delete schedule", "error");
    }
  };

  const loadDetails = async (master) => {
    const { data } = await axios.get(`${baseUrl}/${master.id}/details`);
    setDetails(
      (data.data?.items || []).map((item) => ({
        ...item,
        id: item.internal_schedule_detail_id,
      }))
    );
  };

  const openDetails = async (master) => {
    setSelectedMaster(master);
    try {
      await loadDetails(master);
      setDetailsOpen(true);
    } catch (error) {
      notify(error.response?.data?.message || "Failed to load schedule details", "error");
    }
  };

  const loadYearSemesters = async (master = selectedMaster) => {
    const suffix = master?.program_id ? `/${master.program_id}` : "";
    const { data } = await axios.get(
      `${apiBase}/api/program-year-semesters/${user.client_id}/${user.college_id}${suffix}`
    );
    setYearSemesters(data.data?.items || []);
  };

  const openAddDetail = async (master = selectedMaster) => {
    setSelectedMaster(master);
    try {
      await loadYearSemesters(master);
      setEditingDetail(null);
      setDetailForm({ ...emptyDetail, slotIds: [""], admitted_batches: [] });
      setDetailOpen(true);
    } catch (error) {
      notify(error.response?.data?.message || "Failed to load study years and semesters", "error");
    }
  };

  const openEditDetail = async (detail) => {
    try {
      await loadYearSemesters();
      const selected = String(detail.exam_slot_id || "")
        .split(",")
        .filter(Boolean);
      setEditingDetail(detail);
      setDetailForm({
        year_number: detail.year_number || "",
        semester_number: detail.semester_number || "",
        number_of_subjects: detail.number_of_subjects || selected.length || 1,
        slotIds: selected.length ? selected : [""],
        admitted_batches:
          detail.schedule_batch_maps?.map((map) => String(map.admittedbatch_id)) || [],
      });
      setDetailOpen(true);
    } catch (error) {
      notify(error.response?.data?.message || "Failed to load study years and semesters", "error");
    }
  };

  const changeSubjectCount = (value) => {
    const count = Math.max(1, Math.min(3, Number(value) || 1));
    setDetailForm((current) => ({
      ...current,
      number_of_subjects: count,
      slotIds: Array.from({ length: count }, (_, index) => current.slotIds[index] || ""),
    }));
  };

  const changeSlot = (index, value) => {
    setDetailForm((current) => ({
      ...current,
      slotIds: current.slotIds.map((slotId, slotIndex) => (slotIndex === index ? value : slotId)),
    }));
  };

  const saveDetail = async () => {
    const selected = detailForm.slotIds.filter(Boolean);
    if (!detailForm.year_number || !detailForm.semester_number) {
      notify("Study year and semester are required", "warning");
      return;
    }
    if (!detailForm.admitted_batches?.length) {
      notify("Select at least one admitted batch", "warning");
      return;
    }
    if (
      selected.length !== Number(detailForm.number_of_subjects) ||
      new Set(selected).size !== selected.length
    ) {
      notify("Select one different time slot for every subject", "warning");
      return;
    }
    const payload = {
      ...detailForm,
      exam_slot_id: selected.join(","),
      admitted_batches: detailForm.admitted_batches,
      created_by: user.user_email,
      modified_by: user.user_email,
    };
    try {
      if (editingDetail) {
        await axios.put(`${baseUrl}/details/${editingDetail.id}`, payload);
      } else {
        await axios.post(`${baseUrl}/${selectedMaster.id}/details`, payload);
      }
      setDetailOpen(false);
      await loadDetails(selectedMaster);
      await loadSchedules();
      notify(
        editingDetail
          ? "Semester schedule updated"
          : "Semester schedule saved. An existing year/semester is updated automatically."
      );
    } catch (error) {
      notify(error.response?.data?.message || "Failed to save schedule detail", "error");
    }
  };

  const deleteDetail = async (detail) => {
    if (!window.confirm("Delete this internal schedule detail?")) return;
    try {
      await axios.delete(`${baseUrl}/details/${detail.id}`);
      await loadDetails(selectedMaster);
      notify("Schedule detail deleted");
    } catch (error) {
      notify(error.response?.data?.message || "Failed to delete detail", "error");
    }
  };

  const loadTimetable = async (master) => {
    setTimetableLoading(true);
    try {
      const { data } = await axios.get(`${baseUrl}/${master.id}/timetable`);
      setTimetableRows(
        (data.data?.items || []).map((item) => ({ ...item, id: item.timetable_id }))
      );
      setTimetableCollege(data.data?.items?.[0]?.college || null);
    } finally {
      setTimetableLoading(false);
    }
  };
  const saveTimetableDate = async () => {
    if (!dateEditRow || !dateEditValue) {
      notify("Select an exam date", "warning");
      return;
    }
    setTimetableLoading(true);
    try {
      await axios.put(`${baseUrl}/timetable/${dateEditRow.timetable_id}/date`, {
        from_exam_date: dateEditValue,
        modified_by: user.user_email,
      });
      setDateEditRow(null);
      await loadTimetable(timetableMaster);
      notify("Timetable date updated");
    } catch (error) {
      notify(error.response?.data?.message || "Timetable date update failed", "error");
    } finally {
      setTimetableLoading(false);
    }
  };

  const saveTimetableSlot = async () => {
    if (!slotEditRow || !slotEditValue) {
      notify("Select an exam time slot", "warning");
      return;
    }
    setTimetableLoading(true);
    try {
      await axios.put(`${baseUrl}/timetable/${slotEditRow.timetable_id}/slot`, {
        exam_slot_id: slotEditValue,
        modified_by: user.user_email,
      });
      setSlotEditRow(null);
      await loadTimetable(timetableMaster);
      notify("Timetable time slot updated");
    } catch (error) {
      notify(error.response?.data?.message || "Timetable time slot update failed", "error");
    } finally {
      setTimetableLoading(false);
    }
  };

  const openTimetable = async (master) => {
    setTimetableMaster(master);
    setTimetableOpen(true);
    try {
      await loadTimetable(master);
    } catch (error) {
      notify(error.response?.data?.message || "Failed to load internal timetable", "error");
    }
  };

  const generateTimetable = async () => {
    if (!timetableMaster || !window.confirm("Generate timetable for this internal schedule?")) {
      return;
    }
    setTimetableLoading(true);
    try {
      const { data } = await axios.post(`${baseUrl}/${timetableMaster.id}/timetable/generate`, {
        created_by: user.user_email,
      });
      await loadTimetable(timetableMaster);
      notify(data.message || "Internal timetable generated");
    } catch (error) {
      notify(error.response?.data?.message || "Timetable generation failed", "error");
    } finally {
      setTimetableLoading(false);
    }
  };

  const displayTimetableRows = [
    ...new Map(
      timetableRows.map((row) => [
        `${row.internalscheule_detail_id}|${row.course_code}|${isoDate(row.from_exam_date)}|${
          row.exam_slot_id
        }`,
        row,
      ])
    ).values(),
  ];

  const timetableExportRows = () =>
    displayTimetableRows.map((row, index) => [
      index + 1,
      row.program?.program_name || "",
      row.branch?.branch_name || "",
      row.course_code || "",
      row.exam_course?.course_name || "",
      displayDate(row.from_exam_date),
      row.exam_slot_desc || "",
    ]);

  const downloadTimetablePDF = async () => {
    if (!timetableRows.length) {
      notify("No internal timetable rows are available to download", "warning");
      return;
    }
    try {
      let logoData = null;
      let signatureData = null;

      if (timetableCollege?.logo_data?.data) {
        logoData = `data:${timetableCollege.logo_data.mime_type};base64,${timetableCollege.logo_data.data}`;
      }

      if (timetableCollege?.signature_data?.data) {
        signatureData = `data:${timetableCollege.signature_data.mime_type};base64,${timetableCollege.signature_data.data}`;
      }

      const groups = new Map();
      timetableRows.forEach((row) => {
        const year = row.schedule_detail?.year_number || "";
        const semester = row.schedule_detail?.semester_number || "";
        const key = `${row.program_id}|${row.branch_id}|${year}|${semester}`;
        if (!groups.has(key)) {
          groups.set(key, {
            program: row.program?.program_name || "",
            branch: row.branch?.branch_name || "",
            year,
            semester,
            rows: [],
          });
        }
        groups.get(key).rows.push(row);
      });
      const toRoman = (num) => {
        const romans = ["", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
        return romans[Number(num)] || "";
      };
      const doc = new jsPDF("portrait", "mm", "a4");
      [...groups.values()].forEach((group, groupIndex) => {
        if (groupIndex) doc.addPage();
        const pageWidth = doc.internal.pageSize.getWidth();
        if (logoData) doc.addImage(logoData, 14, 10, 18, 18);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.text(timetableCollege?.college_name || "", pageWidth / 2, 15, { align: "center" });
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        const address = [
          timetableCollege?.college_location,
          timetableCollege?.college_district,
          timetableCollege?.college_state,
          timetableCollege?.college_pincode,
        ]
          .filter(Boolean)
          .join(", ");
        if (address) doc.text(address, pageWidth / 2, 21, { align: "center" });
        const recognition = [
          timetableCollege?.is_autonomous ? "AUTONOMOUS" : "AFFILIATED",
          timetableCollege?.affiliation,
          timetableCollege?.accreditation,
        ]
          .filter(Boolean)
          .join(" | ");
        if (recognition) doc.text(recognition, pageWidth / 2, 27, { align: "center" });
        doc.line(14, 35, pageWidth - 14, 35);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text(
          `${timetableMaster?.component_name || "INTERNAL"} EXAMINATION TIMETABLE - ${
            timetableMaster?.exam_month || ""
          } `,
          pageWidth / 2,
          43,
          { align: "center" }
        );
        doc.setFontSize(10);
        doc.text(`Program: ${group.program}`, 14, 51);
        doc.text(`Branch: ${group.branch}`, 14, 57);
        doc.text(` ${toRoman(group.year)} Year`, pageWidth - 70, 51);
        doc.text(` ${toRoman(group.semester)} Semester`, pageWidth - 70, 57);
        const sortedRows = [...group.rows].sort(
          (first, second) =>
            new Date(first.from_exam_date) - new Date(second.from_exam_date) ||
            String(first.exam_slot_id).localeCompare(String(second.exam_slot_id))
        );
        autoTable(doc, {
          startY: 63,
          head: [["S.No", "Course Code", "Subject Name", "Exam Date", "Exam Time"]],
          body: sortedRows.map((row, index) => [
            index + 1,
            row.course_code || "",
            row.exam_course?.course_name || "",
            displayDate(row.from_exam_date),
            row.exam_slot_desc || "",
          ]),
          styles: { fontSize: 9, valign: "middle" },
          headStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: "bold" },
        });
        const signatureY = Math.min((doc.lastAutoTable?.finalY || 70) + 16, 280);

        if (signatureData) {
          doc.addImage(signatureData, pageWidth - 55, signatureY - 12, 40, 15);
        }

        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);

        doc.text("Principal", pageWidth - 24, signatureY + 6, { align: "right" });
      });
      const safeComponent = String(timetableMaster?.component_name || "Internal")
        .replace(/[^a-z0-9]+/gi, "_")
        .replace(/^_|_$/g, "");
      doc.save(`Internal_Timetable_${safeComponent}.pdf`);
    } catch (error) {
      notify(error.response?.data?.message || "Failed to generate timetable PDF", "error");
    }
  };

  const downloadTimetableCSV = () => {
    if (!displayTimetableRows.length) {
      notify("No internal timetable rows are available to download", "warning");
      return;
    }
    const csvCell = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;
    const rows = [
      ["S.No", "Program", "Course Code", "Subject Name", "Exam Date", "Exam Time"],
      ...timetableExportRows(),
    ];
    const blob = new Blob([`\uFEFF${rows.map((row) => row.map(csvCell).join(",")).join("\r\n")}`], {
      type: "text/csv;charset=utf-8",
    });
    const safeComponent = String(timetableMaster?.component_name || "Internal")
      .replace(/[^a-z0-9]+/gi, "_")
      .replace(/^_|_$/g, "");
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Internal_Timetable_${safeComponent}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const loadSeatingPlan = async (master, form) => {
    const { data } = await axios.get(`${baseUrl}/${master.id}/seating-plan`, {
      params: {
        from_exam_date: form.from_exam_date || undefined,
        exam_slot_id: form.exam_slot_id || undefined,
      },
    });
    setSeatingRows(
      (data.data?.items || []).map((item) => ({ ...item, id: item.room_allocation_id }))
    );
  };

  const openSeatingPlan = async (master, mode = "seating-plan", preset = {}) => {
    setSeatingMode(mode);
    setSeatingMaster(master);
    setSeatingOpen(true);
    setSeatingLoading(true);
    try {
      const { data } = await axios.get(`${baseUrl}/${master.id}/timetable`);
      const items = data.data?.items || [];
      setSeatingTimetables(items);
      const firstDate = preset.from_exam_date || (items[0] ? isoDate(items[0].from_exam_date) : "");
      const firstSlot =
        preset.exam_slot_id ||
        items.find((item) => isoDate(item.from_exam_date) === firstDate)?.exam_slot_id;
      const initialForm = {
        from_exam_date: firstDate,
        exam_slot_id: firstSlot || "",
        columns_per_room: preset.columns_per_room || "",
        invigilator_strength: preset.invigilator_strength || "",
      };
      setSeatingForm(initialForm);
      await loadSeatingPlan(master, initialForm);
    } catch (error) {
      notify(error.response?.data?.message || "Failed to load seating plan", "error");
    } finally {
      setSeatingLoading(false);
    }
  };

  const generateSeatingPlan = async () => {
    if (!seatingForm.from_exam_date || !seatingForm.exam_slot_id) {
      notify("Select an exam date and time slot", "warning");
      return;
    }
    if (
      seatingForm.columns_per_room === "" ||
      !Number.isInteger(Number(seatingForm.columns_per_room)) ||
      Number(seatingForm.columns_per_room) < 0
    ) {
      notify("Enter columns per room", "warning");
      return;
    }
    if (
      seatingForm.invigilator_strength === "" ||
      !Number.isInteger(Number(seatingForm.invigilator_strength)) ||
      Number(seatingForm.invigilator_strength) < 0
    ) {
      notify("Enter a valid invigilator strength threshold", "warning");
      return;
    }
    setSeatingLoading(true);
    try {
      const { data } = await axios.post(`${baseUrl}/${seatingMaster.id}/seating-plan`, {
        ...seatingForm,
        created_by: user.user_email,
      });
      await loadSeatingPlan(seatingMaster, seatingForm);
      notify(data.message || "Seating plan generated");
      await downloadInternalReport("seating-plan", "pdf");
    } catch (error) {
      notify(error.response?.data?.message || "Seating plan generation failed", "error");
    } finally {
      setSeatingLoading(false);
    }
  };

  const changeSeatingSelection = async (changes) => {
    const next = { ...seatingForm, ...changes };
    setSeatingForm(next);
    setSeatingRows([]);
    if (!seatingMaster || !next.from_exam_date || !next.exam_slot_id) return;
    setSeatingLoading(true);
    try {
      await loadSeatingPlan(seatingMaster, next);
    } catch (error) {
      notify(error.response?.data?.message || "Failed to load slot seating plan", "error");
    } finally {
      setSeatingLoading(false);
    }
  };

  const downloadDateReport = async (master, form, reportType, format = "pdf") => {
    if (!form.from_exam_date || !form.exam_slot_id) {
      notify("Select an exam date and time slot", "warning");
      return;
    }
    try {
      const response = await axios.get(`${baseUrl}/${master.id}/report/${reportType}`, {
        params: {
          ...form,
          format,
        },
        responseType: "blob",
      });
      const disposition = response.headers["content-disposition"] || "";
      const filename = disposition.match(/filename=([^;]+)/i)?.[1] || `${reportType}.${format}`;
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename.replaceAll('"', "");
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      let message = error.response?.data?.message;
      if (!message && error.response?.data instanceof Blob) {
        try {
          const payload = JSON.parse(await error.response.data.text());
          message = payload.message;
        } catch {
          message = "";
        }
      }
      notify(message || `Unable to generate ${reportType}`, "error");
    }
  };
  const downloadDaywiseStrength = async () => {
    try {
      const response = await axios.get(`${baseUrl}/${timetableMaster.id}/report/daywise-strength`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Daywise_Strength_Report_${timetableMaster.id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      let message = error.response?.data?.message;
      if (!message && error.response?.data instanceof Blob) {
        try {
          message = JSON.parse(await error.response.data.text()).message;
        } catch {
          message = "";
        }
      }
      notify(message || "Error generating Daywise Strength Report", "error");
    }
  };

  const downloadInternalReport = (reportType, format = "csv") =>
    downloadDateReport(seatingMaster, seatingForm, reportType, format);

  const attendanceModes = {
    "post-absents": { status: "AB", button: "Mark Absent" },
    "post-malpractice": { status: "MP", button: "Mark MalPractice" },
    "post-debar": { status: "DB", button: "Mark Debar" },
  };

  const updateAttendanceStatus = async (row) => {
    const action = attendanceModes[seatingMode];
    if (!action) return;
    const nextStatus =
      action.status === "AB" && row.attendance_status === "AB" ? "" : action.status;
    try {
      await axios.put(
        `${baseUrl}/${seatingMaster.id}/seating-plan/${row.room_allocation_id}/attendance`,
        { status: nextStatus, modified_by: user.user_email }
      );
      setSeatingRows((current) =>
        current.map((item) =>
          item.room_allocation_id === row.room_allocation_id
            ? { ...item, attendance_status: nextStatus }
            : item
        )
      );
      notify(
        nextStatus
          ? `${row.student_rollno} marked as ${action.button.replace("Mark ", "")}`
          : `${row.student_rollno} unmarked as Absent`
      );
    } catch (error) {
      notify(error.response?.data?.message || `Unable to ${action.button.toLowerCase()}`, "error");
    }
  };

  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 75,
      sortable: false,
      filterable: false,
      valueGetter: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
    },
    { field: "component_name", headerName: "Internal Exam Component", flex: 1, minWidth: 100 },
    { field: "program_name", headerName: "Program", flex: 1, minWidth: 100 },
    { field: "exam_month", headerName: "Month", flex: 1, minWidth: 150 },
    { field: "exam_year", headerName: "Year", width: 90 },
    {
      field: "exam_start_date",
      headerName: "Start Date",
      flex: 1,
      minWidth: 130,
      valueGetter: ({ value }) => displayDate(value),
    },
    {
      field: "exam_end_date",
      headerName: "End Date",
      flex: 1,
      minWidth: 130,
      valueGetter: ({ value }) => displayDate(value),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 170,
      sortable: false,
      renderCell: ({ row }) => (
        <RowActions
          row={row}
          onCreateSemester={openAddDetail}
          onViewSemester={openDetails}
          onViewTimetable={openTimetable}
          onEdit={openEditMaster}
          onDelete={deleteMaster}
        />
      ),
    },
  ];

  const selectedYearSemester = yearSemesters.find(
    (item) =>
      Number(item.year_number) === Number(detailForm.year_number) &&
      Number(item.semester_number) === Number(detailForm.semester_number)
  );
  //console.log(selectedYearSemester);
  const academicYearStart = Number(String(selectedYearSemester?.academic_year || "").split("-")[0]);

  const selectedStudyYear = Number(detailForm.year_number);

  const expectedBatchStart =
    academicYearStart && selectedStudyYear ? academicYearStart - selectedStudyYear + 1 : null;

  const filteredAdmittedBatches = expectedBatchStart
    ? admittedBatches.filter(
        (batch) => Number(String(batch.admittedbatch || "").split("-")[0]) === expectedBatchStart
      )
    : admittedBatches;
  const availableAdmittedBatches = [
    ...new Map(
      [
        ...filteredAdmittedBatches,
        ...admittedBatches.filter((batch) =>
          detailForm.admitted_batches.includes(String(batch.admittedbatch_id))
        ),
      ].map((batch) => [String(batch.admittedbatch_id), batch])
    ).values(),
  ];

  const detailColumns = [
    { field: "year_number", headerName: "Study Year Number", flex: 1, minWidth: 120 },
    { field: "semester_number", headerName: "Semester Number", flex: 1, minWidth: 120 },
    { field: "number_of_subjects", headerName: "Courses Per Day", flex: 1, minWidth: 120 },
    {
      field: "admitted_batches",
      headerName: "Admitted Batches",
      flex: 1.2,
      valueGetter: ({ row }) =>
        row.schedule_batch_maps?.map((map) => map.admittedbatch?.admittedbatch).join(", ") || "",
    },
    {
      field: "exam_slot_id",
      headerName: "Exam Time Slots",
      flex: 1.5,
      valueGetter: ({ value }) =>
        String(value || "")
          .split(",")
          .map((id) => {
            const slot = slots.find((item) => String(item.exam_slot_id) === id);
            return slot?.description || slot?.slot_name || id;
          })
          .join(", "),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: ({ row }) => (
        <Stack direction="row">
          <IconButton onClick={() => openEditDetail(row)}>
            <EditIcon />
          </IconButton>
          <IconButton color="error" onClick={() => deleteDetail(row)}>
            <DeleteIcon />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const timetableColumns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 70,
      sortable: false,
      valueGetter: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
    },

    {
      field: "program_name",
      headerName: "Program",
      width: 150,
      valueGetter: ({ row }) => row.program?.program_name || "",
    },
    {
      field: "branch_name",
      headerName: "Branch",
      width: 180,
      valueGetter: ({ row }) => row.branch?.branch_name || "",
    },
    {
      field: "year_number",
      headerName: "Year",
      width: 70,
      valueGetter: ({ row }) => row.schedule_detail?.year_number || "",
    },
    {
      field: "semester_number",
      headerName: "Semester",
      width: 90,
      valueGetter: ({ row }) => row.schedule_detail?.semester_number || "",
    },
    {
      field: "admitted_batches",
      headerName: "Batches",
      minWidth: 100,
      flex: 1,
      valueGetter: ({ row }) =>
        row.schedule_detail?.schedule_batch_maps
          ?.map((mapping) => mapping.admittedbatch?.admittedbatch)
          .filter(Boolean)
          .join(", ") || "",
    },
    { field: "course_code", headerName: "Course Code", width: 120 },
    {
      field: "course_name",
      headerName: "Course Name",
      width: 320,
      valueGetter: ({ row }) => row.exam_course?.course_name || "",
    },
    {
      field: "from_exam_date",
      headerName: "Exam Date",
      width: 180,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" gap={1}>
          <span>{displayDate(row.from_exam_date)}</span>

          <Tooltip title="Change Exam Date">
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                setDateEditRow(row);
                setDateEditValue(isoDate(row.from_exam_date));
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "exam_slot_desc",
      headerName: "Exam Time Slot",
      width: 230,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" gap={1}>
          <span>{row.exam_slot_desc || ""}</span>
          <Tooltip title="Change Exam Time Slot">
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                setSlotEditRow(row);
                setSlotEditValue(String(row.exam_slot_id || ""));
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 250,
      sortable: false,
      renderCell: ({ row }) => (
        <ExamDateActions
          row={row}
          master={timetableMaster}
          onOpen={openSeatingPlan}
          onDownload={downloadDateReport}
          onDaywiseStrength={downloadDaywiseStrength}
        />
      ),
    },
  ];

  const seatingColumns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 70,
      sortable: false,
      valueGetter: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
    },
    { field: "student_rollno", headerName: "Student Roll No.", flex: 1 },
    {
      field: "branch_name",
      headerName: "Branch",
      flex: 1,
      valueGetter: ({ row }) => row.branch?.branch_name || "",
    },
    {
      field: "course_code",
      headerName: "Course Code",
      width: 120,
      valueGetter: ({ row }) => row.timetable?.course_code || "",
    },
    {
      field: "room_no",
      headerName: "Room",
      width: 90,
      valueGetter: ({ row }) => row.room?.room_no || "",
    },
    { field: "bench_no", headerName: "Bench", width: 80 },
    { field: "seat_no", headerName: "Seat", width: 80 },
    { field: "exam_slot_id", headerName: "Exam Slot", width: 100 },
  ];

  const statusAction = attendanceModes[seatingMode];
  const attendanceRows = seatingRows.filter((row) => {
    const value = attendanceSearch.trim().toLowerCase();
    if (!value) return true;
    return [row.student_rollno, row.student?.student_name, row.branch?.branch_name]
      .filter(Boolean)
      .some((item) => String(item).toLowerCase().includes(value));
  });
  const attendanceColumns = [
    { field: "student_rollno", headerName: "Roll No", flex: 1 },
    {
      field: "student_name",
      headerName: "Student Name",
      flex: 1.5,
      valueGetter: ({ row }) => row.student?.student_name || "",
    },
    {
      field: "branch_name",
      headerName: "Branch",
      flex: 1,
      valueGetter: ({ row }) => row.branch?.branch_name || "",
    },
    { field: "attendance_status", headerName: "Status", width: 100 },
    {
      field: "attendance_action",
      headerName: "Actions",
      width: 180,
      sortable: false,
      renderCell: ({ row }) => (
        <Button
          size="medium"
          disabled={statusAction?.status !== "AB" && row.attendance_status === statusAction?.status}
          onClick={() => updateAttendanceStatus(row)}
        >
          {statusAction?.status === "AB" && row.attendance_status === "AB"
            ? "Unmark Absent"
            : statusAction?.button}
        </Button>
      ),
    },
  ];

  const seatingDates = [
    ...new Set(seatingTimetables.map((item) => isoDate(item.from_exam_date))),
  ].filter(Boolean);
  const seatingSlotIds = [
    ...new Set(
      seatingTimetables
        .filter((item) => isoDate(item.from_exam_date) === seatingForm.from_exam_date)
        .map((item) => String(item.exam_slot_id))
    ),
  ];
  const seatingModeLabels = {
    "seating-plan": "Seating Plan",
    abstract: "Abstract Seating Plan",
    "room-course-strength": "Room Course Strength",
    "attendance-sheet": "Attendance Sheet",
    "view-invigilators": "View Invigilators",
    "post-absents": "Post Absents",
    "post-malpractice": "Post MalPractice",
    "post-debar": "Post Debar",
    dform: "DForm",
  };

  const runSelectedSeatingAction = () => {
    if (seatingMode === "seating-plan") return generateSeatingPlan();
    return downloadInternalReport(seatingMode, "csv");
  };

  const normalizedSearch = search.trim().toLowerCase();
  const filteredRows = normalizedSearch
    ? rows.filter((row) =>
        [
          row.component_name,
          row.program_name,
          row.exam_mode_name,
          row.exam_month,
          row.exam_year,
          row.workflow_status,
          row.remarks,
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(normalizedSearch)
        )
      )
    : rows;

  return (
    <Box p={2} sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden" }}>
      <Paper elevation={1} sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap", py: 1 }}>
          <Typography variant="h5" sx={{ flexGrow: 1, minWidth: { xs: "100%", md: 240 } }}>
            INTERNAL EXAM SCHEDULE MASTER
          </Typography>
          <TextField
            size="medium"
            placeholder="Search schedules"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: { xs: "1 1 100%", sm: "1 1 240px" }, maxWidth: { sm: 320 } }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddMaster}
            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
          >
            Add Internal Exam Schedule
          </Button>
        </Toolbar>
        <Box
          sx={{
            height: 560,
            width: "calc(100% - 32px)",
            mx: 2,
            minWidth: 0,
          }}
          mt={2}
          mr={2}
        >
          <DataGrid
            rows={filteredRows}
            columns={columns}
            loading={loading}
            pageSize={pageSize}
            page={page}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            pagination
            disableSelectionOnClick
          />
        </Box>
      </Paper>

      <Dialog open={masterOpen} onClose={() => setMasterOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMaster ? "Edit Internal Exam Schedule" : "Add Internal Exam Schedule"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                size="medium"
                label="Program (Optional for all programs)"
                value={masterForm.program_id || ""}
                onChange={(e) => setMasterForm({ ...masterForm, program_id: e.target.value })}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
              >
                <option value="">All Programs</option>
                {programs.map((p) => (
                  <option key={p.program_id} value={p.program_id}>
                    {p.program_name}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                size="medium"
                required
                label="Internal Exam Component"
                value={masterForm.component_id || ""}
                onChange={(e) => setMasterForm({ ...masterForm, component_id: e.target.value })}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
              >
                <option value="">Select Component</option>
                {components.map((c) => (
                  <option key={c.component_id} value={c.component_id}>
                    {c.component_name}
                    {c.mid_number ? ` - Mid ${c.mid_number}` : ""}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                required
                size="medium"
                label="Exam Mode"
                value={masterForm.exam_mode_id || ""}
                onChange={(e) => setMasterForm({ ...masterForm, exam_mode_id: e.target.value })}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
              >
                <option value="">Select Exam Mode</option>
                {examModes.map((m) => (
                  <option key={m.exam_mode_id} value={m.exam_mode_id}>
                    {m.exam_mode_name}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                required
                size="medium"
                label="Exam Month"
                value={masterForm.exam_month}
                onChange={(e) => {
                  const examMonth = e.target.value;
                  setMasterForm({
                    ...masterForm,
                    exam_month: examMonth,
                    exam_year: examMonth ? Number(examMonth.split("-")[1]) : masterForm.exam_year,
                  });
                }}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
              >
                <option value="">Select Exam Month</option>
                {examMonths.map((month) => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                size="medium"
                type="number"
                label="Exam Year"
                value={masterForm.exam_year}
                onChange={(e) => setMasterForm({ ...masterForm, exam_year: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                size="medium"
                type="date"
                label="Exam Start Date"
                InputLabelProps={{ shrink: true }}
                value={masterForm.exam_start_date}
                onChange={(e) => setMasterForm({ ...masterForm, exam_start_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                size="medium"
                type="date"
                label="Exam End Date"
                InputLabelProps={{ shrink: true }}
                value={masterForm.exam_end_date}
                onChange={(e) => setMasterForm({ ...masterForm, exam_end_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TextField
                  label="Excluded Dates"
                  value={
                    (masterForm.exclude_dates || []).map(displayDate).join(", ") ||
                    "No dates selected"
                  }
                  fullWidth
                  size="medium"
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  onClick={() => setExcludeDialogOpen(true)}
                />
                <Dialog
                  open={excludeDialogOpen}
                  onClose={() => setExcludeDialogOpen(false)}
                  maxWidth="md"
                >
                  <DialogTitle>Select Exclude Dates</DialogTitle>
                  <DialogContent>
                    <DateCalendar
                      value={null}
                      minDate={
                        masterForm.exam_start_date ? dayjs(masterForm.exam_start_date) : dayjs()
                      }
                      maxDate={
                        masterForm.exam_end_date
                          ? dayjs(masterForm.exam_end_date)
                          : dayjs().add(3, "month")
                      }
                      displayedMonths={3}
                      onChange={handleExcludedDateChange}
                    />
                    <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
                      {(masterForm.exclude_dates || []).map((date) => (
                        <Chip
                          key={date}
                          label={displayDate(date)}
                          onDelete={() =>
                            setMasterForm((current) => ({
                              ...current,
                              exclude_dates: current.exclude_dates.filter((item) => item !== date),
                            }))
                          }
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setExcludeDialogOpen(false)}>Done</Button>
                  </DialogActions>
                </Dialog>
              </LocalizationProvider>
              {(masterForm.exclude_dates || []).length > 0 && (
                <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                  {masterForm.exclude_dates.map((date) => (
                    <Chip
                      key={date}
                      label={displayDate(date)}
                      onDelete={() =>
                        setMasterForm((current) => ({
                          ...current,
                          exclude_dates: current.exclude_dates.filter((item) => item !== date),
                        }))
                      }
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Remarks"
                multiline
                rows={2}
                fullWidth
                size="medium"
                value={masterForm.remarks || ""}
                onChange={(e) => setMasterForm({ ...masterForm, remarks: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMasterOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveMaster}
            sx={{ color: "#fff", "&:hover": { color: "#fff" } }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Internal Semester Schedule - {selectedMaster?.component_name}</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openAddDetail()}>
              Create Schedule
            </Button>
          </Box>
          <DataGrid
            autoHeight
            rows={details}
            columns={detailColumns}
            pagination
            pageSize={5}
            rowsPerPageOptions={[5, 10]}
            disableSelectionOnClick
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDetailsOpen(false)}
            sx={{
              minWidth: 80,
              color: "#fff",
              backgroundColor: "#d32f2f",
              "&:hover": {
                backgroundColor: "#b71c1c",
                color: "#fff",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDetail ? "Edit" : "Create"} Schedule for {selectedMaster?.exam_mode_name} -{" "}
          {selectedMaster?.exam_month}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              select
              required
              fullWidth
              size="medium"
              label="Study Year"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
              sx={seatingControlSx}
              value={detailForm.year_number}
              onChange={(e) =>
                setDetailForm({
                  ...detailForm,
                  year_number: e.target.value,
                  semester_number: "",
                  admitted_batches: [],
                })
              }
            >
              <MenuItem value="" disabled>
                Select Year
              </MenuItem>
              {[...new Set(yearSemesters.map((item) => item.year_number))].map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              required
              fullWidth
              size="medium"
              label="Semester"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
              sx={seatingControlSx}
              value={detailForm.semester_number}
              onChange={(e) =>
                setDetailForm({
                  ...detailForm,
                  semester_number: e.target.value,
                })
              }
            >
              <MenuItem value="" disabled>
                Select Semester
              </MenuItem>

              {[
                ...new Set(
                  yearSemesters
                    .filter((item) => String(item.year_number) === String(detailForm.year_number))
                    .map((item) => item.semester_number)
                ),
              ].map((semester) => (
                <MenuItem key={semester} value={semester}>
                  {semester}
                </MenuItem>
              ))}
            </TextField>
            <Autocomplete
              multiple
              options={availableAdmittedBatches}
              getOptionLabel={(option) => option.admittedbatch || ""}
              value={availableAdmittedBatches.filter((batch) =>
                detailForm.admitted_batches.includes(String(batch.admittedbatch_id))
              )}
              isOptionEqualToValue={(option, value) =>
                String(option.admittedbatch_id) === String(value.admittedbatch_id)
              }
              onChange={(event, values) =>
                setDetailForm({
                  ...detailForm,
                  admitted_batches: values.map((batch) => String(batch.admittedbatch_id)),
                })
              }
              renderTags={(values, getTagProps) =>
                values.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.admittedbatch_id}
                    label={option.admittedbatch}
                    color="primary"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  size="medium"
                  label="Admitted Batches"
                  placeholder="Select Admitted Batches"
                  InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                  sx={{
                    "& .MuiInputLabel-root": { bgcolor: "background.paper", px: 0.5 },
                    "& .MuiOutlinedInput-root": { minHeight: 40 },
                  }}
                />
              )}
            />
            <TextField
              select
              required
              fullWidth
              size="medium"
              label="Number of Courses Per Day"
              InputLabelProps={{ shrink: true }}
              sx={seatingControlSx}
              value={detailForm.number_of_subjects}
              onChange={(e) => changeSubjectCount(e.target.value)}
            >
              {[1, 2, 3].map((count) => (
                <MenuItem key={count} value={count}>
                  {count}
                </MenuItem>
              ))}
            </TextField>
            {detailForm.slotIds.map((slotId, index) => (
              <TextField
                key={index}
                select
                required
                fullWidth
                size="medium"
                label={`Subject ${index + 1} - Exam Time Slot`}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                sx={seatingControlSx}
                value={slotId}
                onChange={(e) => changeSlot(index, e.target.value)}
              >
                <MenuItem value="" disabled>
                  Select Exam Time
                </MenuItem>
                {slots.map((slot) => (
                  <MenuItem
                    key={slot.exam_slot_id}
                    value={String(slot.exam_slot_id)}
                    disabled={detailForm.slotIds.some(
                      (selected, selectedIndex) =>
                        selectedIndex !== index && selected === String(slot.exam_slot_id)
                    )}
                  >
                    {slot.slot_name} {slot.description ? `- ${slot.description}` : ""}
                  </MenuItem>
                ))}
              </TextField>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveDetail}
            sx={{ color: "#fff", "&:hover": { color: "#fff" } }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={timetableOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }

          setTimetableOpen(false);
        }}
        disableEscapeKeyDown
        maxWidth="xxl"
        fullWidth
      >
        <DialogTitle sx={{ position: "relative", pr: 6 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              SHOW EXAM DATES FOR <strong>{timetableMaster?.component_name}</strong> -{" "}
              <strong>
                {timetableMaster?.exam_month} {timetableMaster?.exam_year}
              </strong>
            </Box>
          </Box>

          {/* Close X button */}
          <IconButton
            onClick={() => setTimetableOpen(false)}
            aria-label="close"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#d32f2f",
              backgroundColor: "#ffebee",
              "&:hover": {
                backgroundColor: "#ffcdd2",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "stretch", sm: "flex-end" },
              alignItems: { xs: "stretch", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              mt: 1,
              mb: 2,
              gap: 1.5,
              width: "100%",
            }}
          >
            <Button
              variant="contained"
              onClick={generateTimetable}
              sx={{
                minWidth: { xs: "100%", sm: 190 },
                color: "#fff",
                backgroundColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#1565c0",
                  color: "#fff",
                },
              }}
            >
              Generate Timetable
            </Button>

            <Button
              variant="contained"
              onClick={downloadTimetablePDF}
              sx={{
                minWidth: { xs: "100%", sm: 80 },
                color: "#fff",
                backgroundColor: "#d32f2f",
                "&:hover": {
                  backgroundColor: "#b71c1c",
                  color: "#fff",
                },
              }}
            >
              PDF
            </Button>

            <Button
              variant="contained"
              onClick={downloadTimetableCSV}
              sx={{
                minWidth: { xs: "100%", sm: 80 },
                color: "#fff",
                backgroundColor: "#2e7d32",
                "&:hover": {
                  backgroundColor: "#1b5e20",
                  color: "#fff",
                },
              }}
            >
              CSV
            </Button>
          </Box>

          <DataGrid
            autoHeight
            rows={displayTimetableRows}
            columns={timetableColumns}
            loading={timetableLoading}
            pagination
            page={page}
            onPageChange={(newPage) => setPage(newPage)}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            disableSelectionOnClick
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTimetableOpen(false)}
            sx={{
              minWidth: 80,
              color: "#fff",
              backgroundColor: "#d32f2f",
              "&:hover": {
                backgroundColor: "#b71c1c",
                color: "#fff",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(dateEditRow)}
        onClose={() => setDateEditRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Change Timetable Date</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {dateEditRow?.course_code || "Selected course"}
          </Typography>
          <TextField
            fullWidth
            required
            type="date"
            label="Exam Date"
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: timetableMaster?.exam_start_date
                ? isoDate(timetableMaster.exam_start_date)
                : undefined,
              max: timetableMaster?.exam_end_date
                ? isoDate(timetableMaster.exam_end_date)
                : undefined,
            }}
            value={dateEditValue}
            onChange={(event) => setDateEditValue(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDateEditRow(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveTimetableDate} disabled={timetableLoading}>
            Update Date
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(slotEditRow)}
        onClose={() => setSlotEditRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Change Timetable Time Slot</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {slotEditRow?.course_code || "Selected course"}
          </Typography>
          <TextField
            select
            fullWidth
            required
            label="Exam Time Slot"
            value={slotEditValue}
            onChange={(event) => setSlotEditValue(event.target.value)}
          >
            {slots.map((slot) => (
              <MenuItem key={slot.exam_slot_id} value={String(slot.exam_slot_id)}>
                {slot.slot_name} {slot.description ? `- ${slot.description}` : ""}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setSlotEditRow(null)}>
            Cancel
          </Button>
          <Button onClick={saveTimetableSlot} disabled={timetableLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {statusAction && (
        <InternalPostStatusDialog
          open={seatingOpen}
          onClose={() => setSeatingOpen(false)}
          baseUrl={baseUrl}
          masterId={seatingMaster?.id}
          programName={seatingMaster?.program_name || ""}
          examDate={seatingForm.from_exam_date}
          examSlotId={seatingForm.exam_slot_id}
          slotDescription={
            slots.find((item) => String(item.exam_slot_id) === String(seatingForm.exam_slot_id))
              ?.description || ""
          }
          status={statusAction.status}
          title={seatingModeLabels[seatingMode]}
          buttonLabel={statusAction.button}
          modifiedBy={user.user_email}
          showSnackbar={notify}
        />
      )}
      {seatingMode === "view-invigilators" && (
        <StaffAllocationDialog
          open={seatingOpen}
          onClose={() => setSeatingOpen(false)}
          examScheduleMasterId={seatingMaster?.id}
          exam_slot_id={seatingForm.exam_slot_id}
          exam_slot_desc={
            slots.find((item) => String(item.exam_slot_id) === String(seatingForm.exam_slot_id))
              ?.description || ""
          }
          from_exam_date={seatingForm.from_exam_date}
          showSnackbar={notify}
          internalBaseUrl={`${baseUrl}/${seatingMaster?.id}/room-staff-allocations`}
        />
      )}
      <Dialog
        open={seatingOpen && !statusAction && seatingMode !== "view-invigilators"}
        onClose={() => setSeatingOpen(false)}
        maxWidth="xxl"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          INTERNAL {seatingModeLabels[seatingMode]?.toUpperCase()} - {seatingMaster?.component_name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5} mb={2} alignItems="center">
            <Grid item xs={12} sm={6} md={seatingMode === "seating-plan" ? 3 : 4}>
              <TextField
                select
                fullWidth
                size="medium"
                label="Exam Date"
                InputLabelProps={{ shrink: true }}
                sx={seatingControlSx}
                value={seatingForm.from_exam_date}
                onChange={(event) => {
                  const date = event.target.value;
                  const slot = seatingTimetables.find(
                    (item) => isoDate(item.from_exam_date) === date
                  )?.exam_slot_id;
                  changeSeatingSelection({
                    from_exam_date: date,
                    exam_slot_id: slot || "",
                  });
                }}
              >
                {seatingDates.map((date) => (
                  <MenuItem key={date} value={date}>
                    {displayDate(date)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={seatingMode === "seating-plan" ? 3 : 4}>
              <TextField
                select
                fullWidth
                size="medium"
                label="Exam Time Slot"
                InputLabelProps={{ shrink: true }}
                sx={seatingControlSx}
                value={seatingForm.exam_slot_id}
                onChange={(event) => changeSeatingSelection({ exam_slot_id: event.target.value })}
              >
                {seatingSlotIds.map((slotId) => {
                  const slot = slots.find((item) => String(item.exam_slot_id) === slotId);
                  return (
                    <MenuItem key={slotId} value={slotId}>
                      {slot?.description || `Slot ${slotId}`}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
            {seatingMode === "seating-plan" && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="medium"
                  type="number"
                  label="Columns per Room"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0, step: 1 }}
                  sx={seatingControlSx}
                  value={seatingForm.columns_per_room}
                  onChange={(event) =>
                    setSeatingForm({
                      ...seatingForm,
                      columns_per_room: event.target.value,
                    })
                  }
                />
              </Grid>
            )}
            {seatingMode === "seating-plan" && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="medium"
                  type="number"
                  label="Invigilator Strength"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0, step: 1 }}
                  sx={seatingControlSx}
                  value={seatingForm.invigilator_strength}
                  onChange={(event) =>
                    setSeatingForm({ ...seatingForm, invigilator_strength: event.target.value })
                  }
                />
              </Grid>
            )}
            {seatingMode !== "abstract" && !statusAction && (
              <Grid item xs={12} sm={6} md={seatingMode === "seating-plan" ? 3 : 4}>
                <Button
                  fullWidth
                  onClick={runSelectedSeatingAction}
                  sx={{
                    minHeight: 40,
                    whiteSpace: "nowrap",
                    color: "#fff",
                    "&:hover": { color: "#fff" },
                  }}
                >
                  {seatingMode === "seating-plan"
                    ? "Generate Seating Plan"
                    : seatingModeLabels[seatingMode]}
                </Button>
              </Grid>
            )}
          </Grid>
          {seatingMode === "seating-plan" && (
            <DataGrid
              autoHeight
              rows={seatingRows}
              columns={seatingColumns}
              loading={seatingLoading}
              pagination
              pageSize={10}
              rowsPerPageOptions={[5, 10, 25]}
              disableSelectionOnClick
            />
          )}
          {statusAction && (
            <>
              <TextField
                fullWidth
                size="medium"
                label="Search Roll No / Student Name"
                InputLabelProps={{ shrink: true }}
                value={attendanceSearch}
                onChange={(event) => setAttendanceSearch(event.target.value)}
                sx={{ ...seatingControlSx, mb: 2 }}
              />
              <DataGrid
                autoHeight
                rows={attendanceRows}
                columns={attendanceColumns}
                loading={seatingLoading}
                pagination
                pageSize={10}
                rowsPerPageOptions={[5, 10, 25]}
                disableSelectionOnClick
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {["seating-plan", "abstract", "room-course-strength"].includes(seatingMode) && (
            <>
              <Button onClick={() => downloadInternalReport(seatingMode, "pdf")}>PDF</Button>
              <Button onClick={() => downloadInternalReport(seatingMode, "csv")}>CSV</Button>
            </>
          )}
          <Button
            onClick={() => setSeatingOpen(false)}
            sx={{
              minWidth: 80,
              color: "#fff",
              backgroundColor: "#d32f2f",
              "&:hover": {
                backgroundColor: "#b71c1c",
                color: "#fff",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notice.open}
        autoHideDuration={5000}
        onClose={() => setNotice({ ...notice, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={notice.severity} onClose={() => setNotice({ ...notice, open: false })}>
          {notice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
